import React, { Fragment, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useReactToPrint } from 'react-to-print';
import QRCode from "react-qr-code"

import { formatMoney, formatDate, externalCourierStatus, regexPhoneNumber, copyText, notify } from 'helpers/helpers'
import { buildChequeLogMessage } from 'helpers/chequeLogMessage'
import { composeConstructorLines, compositionText } from 'helpers/constructorDisplay'

// Подписи цены строки состава конструктора (кабинет только по-русски)
const PRICE_KIND_LABELS = { free: 'бесплатно', included: 'входит в цену' }
const composeLabels = (key) => ({ price_kind_free: PRICE_KIND_LABELS.free, price_kind_included: PRICE_KIND_LABELS.included }[key] || '')

function priceKindLabel(item) {
	return PRICE_KIND_LABELS[item?.price_kind] || ''
}

function Rightbar({ showRightBar, setShowRightBar, selectedItem }) {
	const { t } = useTranslation()
	const printRef = useRef();

	const handlePrint = useReactToPrint({
		content: () => printRef.current,
	});

	function generateLogMessage(params) {
		return buildChequeLogMessage(params, t)
	}

	// Источник записи без автора: название интеграции или облако
	function logSourceLabel(log) {
		if (log.source_type === 'integration' && log.source_name) return log.source_name
		if (log.source_type === 'cloud') return t('cheque_log_source_cloud')
		return t('cheque_log_source_integration')
	}

	// Текущий статус заявки внешней службы — последняя строка истории
	function lastExternalCourierStatus(cheque) {
		const statuses = cheque?.courier_statuses ?? []
		return statuses.length ? statuses[statuses.length - 1].status : null
	}

	// ID последней заявки службы: по нему её ищут в кабинете Uzum Vendor / Яндекс Доставки
	function lastExternalCourierClaimId(cheque) {
		const statuses = (cheque?.courier_statuses ?? []).filter(item => !!item?.external_id)
		return statuses.length ? String(statuses[statuses.length - 1].external_id) : null
	}

	// Номер заявки у службы, понятный её операторам и курьерам (у Uzum «102602-024712»).
	// Яндекс такого номера не отдаёт — тогда строки нет
	function lastExternalCourierDisplayId(cheque) {
		const statuses = (cheque?.courier_statuses ?? []).filter(item => !!item?.external_id)
		const last = statuses.length ? statuses[statuses.length - 1] : null
		const withDisplay = statuses.filter(item => !!item?.display_id && item.external_id === last?.external_id)
		return withDisplay.length ? String(withDisplay[withDisplay.length - 1].display_id) : null
	}

	// Единственный номер заявки для сотрудника: номер у службы (Uzum, Noor), без него — ID заявки
	// (Яндекс). Внутренний ID там, где есть номер для людей, только путает: по нему заявку не ищут
	function lastExternalCourierNumber(cheque) {
		const displayId = lastExternalCourierDisplayId(cheque)
		if (displayId) return { value: displayId, isDisplay: true }

		const claimId = lastExternalCourierClaimId(cheque)
		return claimId ? { value: claimId, isDisplay: false } : null
	}

	function copyClaimId(claimId) {
		copyText(claimId)
		notify(t('copied'), 'success')
	}

	// Сколько заявка провела в предыдущем статусе — в том же виде ЧЧ:ММ, что у статусов чека
	function courierStatusDuration(statuses, index) {
		if (index === 0) return '00:00'
		const minutes = Math.max(0, Math.round(
			(new Date(statuses[index].created_at) - new Date(statuses[index - 1].created_at)) / 60000
		))
		if (Number.isNaN(minutes)) return ''
		return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
	}

	// В истории, в отличие от итогового статуса, создание и подтверждение заявки
	// показываем отдельными строками — по ним видно, когда её отправили
	function externalCourierHistoryKey(status) {
		if (Number(status) === -10) return 'ext_courier_created'
		if (Number(status) === 1) return 'ext_courier_confirmed'
		return externalCourierStatus(status).key
	}

	// Позиции возврата приходят плоским списком, но принадлежат документам возврата:
	// собираем по документу, чтобы дата печаталась один раз заголовком, а не под каждой строкой
	function groupReturns(products = []) {
		const groups = []
		const byReturnId = {}

		for (const product of products) {
			const key = product.cheque_return_id ?? 'unknown'
			if (!byReturnId[key]) {
				byReturnId[key] = { id: key, created_at: product.created_at, products: [] }
				groups.push(byReturnId[key])
			}
			byReturnId[key].products.push(product)
		}

		return groups
	}

	return (
		<>
			{showRightBar &&
				<>
					<div className="right-sidebar-background"
						onClick={() => setShowRightBar(!showRightBar)}>
					</div>
					<div className="right-sidebar">
						<div className="position-relative px-2">
							<div className="position-relative">
								<div className="rightbar-close-icon" onClick={() => setShowRightBar(!showRightBar)}>
									<i className="uil uil-times"></i>
								</div>
							</div>
							<div className="cheque-block-header cheque-border-bottom mb-2">
								<div className="d-flex justify-content-center">
									<div className="d-flex flex-column">
										<h5 className="text-center fw-700 mb-1">{t('duplicate')}</h5>
										<h5 className="text-center fw-700 mb-1">{selectedItem.pos_name}</h5>
										{selectedItem.pos_phone &&
											<h6 className="text-center fw-600 mb-1">{t('phone')}: {selectedItem.pos_phone}</h6>
										}
										{selectedItem.pos_address &&
											<h6 className="text-center fw-500 mb-1">{t('address')}: {selectedItem.pos_address}</h6>
										}
									</div>
								</div>

								<div className="cheque-block-info">
									{selectedItem.shift_id &&
										<div className="d-flex justify-content-between">
											<p className="fw-600">{t('shift')} ID</p>
											<p>{selectedItem.shift_id}</p>
										</div>
									}
									<div className="d-flex justify-content-between">
										<p className="fw-600">{t('cashier')}</p>
										<p>{selectedItem.cashier_name}</p>
									</div>
									{!!selectedItem.waiter_name &&
										<div className="d-flex justify-content-between">
											<p className="fw-600">{t('waiter')}</p>
											<p>{selectedItem.waiter_name}</p>
										</div>
									}
									{!!selectedItem.operator_name &&
										<div className="d-flex justify-content-between">
											<p className="fw-600">{t('operator')}</p>
											<p>{selectedItem.operator_name}</p>
										</div>
									}
									{selectedItem.courier_name &&
										<div className="d-flex justify-content-between">
											<p className="fw-600">{t('courier')}</p>
											<p>{selectedItem.courier_name}</p>
										</div>
									}
									{/* Внешняя служба доставки: что с заявкой и кто везёт */}
									{!!selectedItem.external_courier_id &&
										<>
											<div className="d-flex justify-content-between">
												<p className="fw-600">{selectedItem.external_courier_name ?? t('external_courier')}</p>
												<p className={`fw-600 ${externalCourierStatus(lastExternalCourierStatus(selectedItem)).color}`}>
													{t(externalCourierStatus(lastExternalCourierStatus(selectedItem)).key)}
												</p>
											</div>
											{!!selectedItem?.external_courier_info?.courierName &&
												<div className="d-flex justify-content-between">
													<p className="fw-600">{t('courier')}</p>
													<p>{selectedItem.external_courier_info.courierName}</p>
												</div>
											}
											{(!!selectedItem?.external_courier_info?.phoneNumber || !!selectedItem?.external_courier_info?.trackingUrl) &&
												<div className="d-flex justify-content-between">
													<p className="fw-600">{t('phone')}</p>
													<p className="d-flex align-items-center gap-2">
														{!!selectedItem?.external_courier_info?.phoneNumber &&
															<span>+{regexPhoneNumber(selectedItem.external_courier_info.phoneNumber)}</span>
														}
														{!!selectedItem?.external_courier_info?.trackingUrl &&
															<a href={selectedItem.external_courier_info.trackingUrl}
																target="_blank" rel="noreferrer"
																title={t('courier_tracking')}
																className="text-primary fs-5 lh-1">
																<i className="uil uil-map-marker-alt"></i>
															</a>
														}
													</p>
												</div>
											}
											{!!lastExternalCourierNumber(selectedItem) &&
												<div className="d-flex justify-content-between gap-2">
													<p className="fw-600 text-nowrap">{lastExternalCourierNumber(selectedItem).isDisplay ? t('external_courier_display_id') : t('external_courier_claim')}</p>
													<p className="d-flex align-items-center gap-2 text-end text-break">
														<span>{lastExternalCourierNumber(selectedItem).value}</span>
														<i className="uil uil-copy text-primary cursor-pointer"
															title={t('copy')}
															onClick={() => copyClaimId(lastExternalCourierNumber(selectedItem).value)}></i>
													</p>
												</div>
											}
										</>
									}
									{!!selectedItem.hall_name &&
										<div className="d-flex justify-content-between">
											<p className="fw-600">{t('hall')}</p>
											<p>{selectedItem.hall_name}</p>
										</div>
									}
									{!!selectedItem.table_name &&
										<div className="d-flex justify-content-between">
											<p className="fw-600">{t('table')}</p>
											<p>{selectedItem.table_name}</p>
										</div>
									}
									<div className="d-flex justify-content-between">
										<p className="fw-600">{t('cheque')} ID </p>
										<p>{selectedItem.cheque_id}</p>
									</div>
									<div className="d-flex justify-content-between">
										<p className="fw-600">{t('cheque')} № </p>
										<p>{selectedItem.cheque_number}</p>
									</div>
									<div className="d-flex justify-content-between">
										<p className="fw-600">{t('guests')}</p>
										<p>{selectedItem.people_number}</p>
									</div>
									{selectedItem.tin &&
										<div className="d-flex justify-content-between">
											<p className="fw-600">{t('tin')}</p>
											<p>{selectedItem.tin}</p>
										</div>
									}
									<div className="d-flex justify-content-between">
										<p className="fw-600">{t('order_type')} </p>
										{selectedItem.order_type_name}
									</div>
									<div className="d-flex justify-content-between">
										<p className="fw-600">{t('delivery_type')} </p>
										{selectedItem.order_type_name}
									</div>
									<div className="d-flex justify-content-between">
										<p className="fw-600">{t('open_date')}</p>
										<p>{formatDate(selectedItem.cheque_opened_at, 'dd.MM.yy HH:mm')}</p>
									</div>
									<div className="d-flex justify-content-between">
										<p className="fw-600">{t('close_date')}</p>
										<p>{formatDate(selectedItem.cheque_closed_at, 'dd.MM.yy HH:mm')}</p>
									</div>
								</div>
							</div>

							<div className="cheque-block-products cheque-border-bottom mb-2">
								<table className="custom-cheque-table w-100 fz-14">
									<thead>
										<tr>
											<th className="w-50"># {t('product')}</th>
											<th>{t('quantity')}</th>
											<th className="text-end">{t('price')}</th>
										</tr>
									</thead>
									<tbody>
										{selectedItem?.products?.length > 0 &&
											<>
												{selectedItem?.products?.map((item, index) => (
													<Fragment key={index}>
														<tr className={`${item.status === -1 ? 'del' : ''}`}>
															{/* column 1 */}
															<td style={{ 'whiteSpace': 'break-spaces' }}>
																<div>
																	{index + 1}.{' '}
																	{/* переход на карточку продукта: склад, метод списания */}
																	{(item.product_id && selectedItem.pos_id) ?
																		<Link to={`/products/update/${selectedItem.pos_id}/${item.product_id}`}
																			target="_blank" rel="noopener noreferrer"
																			title={t('product')}>
																			{item.name}
																		</Link>
																		:
																		<>{item.name}</>
																	}
																</div>
																{priceKindLabel(item) && <div className="ms-2 text-success fz12">{priceKindLabel(item)}</div>}
																{Number(item.discount) > 0 && <div className="ms-2">{t('discount')}</div>}
															</td>
															{/* column 1 */}

															{/* column 2 */}
															<td className="text-left" style={{ 'verticalAlign': 'top' }}>
																<div className="text-nowrap" >
																	{formatMoney(item.quantity, 2)} X {formatMoney(item.sale_price)}
																</div>
															</td>
															{/* column 2 */}

															{/* column 3 */}
															<td className="text-end text-nowrap" style={{ 'verticalAlign': 'top' }}>
																<div>{formatMoney(item.total_price)}</div>
																{Number(item.discount) > 0 && <div>{formatMoney(item.discount)}</div>}
															</td>
															{/* column 3 */}
														</tr>
														<tr>
															<td colSpan={3} style={{ 'lineHeight': '14px' }}>
																{item.deletion_reason_name &&
																	<div className="fz12">
																		<b className="me-2">{t('deletion_reason')}:</b>
																		<span>{item.deletion_reason_name}</span>
																	</div>
																}
																{item.deletion_reason_comment &&
																	<div className="fz12">
																		<b className="me-2">{t('comment')}:</b>
																		<span>{item.deletion_reason_comment}</span>
																	</div>
																}
															</td>
														</tr>
													</Fragment>
												))}
											</>
										}
									</tbody>
								</table>
							</div>

							{/* Возвращённые позиции — отдельным блоком: в самом чеке они не строки,
								а документы возврата со своей датой */}
							{selectedItem?.returns_products?.length > 0 &&
								<div className="cheque-block-products cheque-border-bottom mb-2">
									<p className="fw-600 mb-1">{t('returned_products')}</p>
									{/* Одна таблица на все возвраты, а дата — строкой внутри неё:
										отдельные таблицы на каждую дату не совпадали колонками с составом чека */}
									<table className="custom-cheque-table w-100 fz-14">
										<tbody>
											{groupReturns(selectedItem.returns_products).map((group) => (
												<Fragment key={group.id}>
													<tr>
														<td colSpan={3} className="fz12 text-secondary">
															{formatDate(group.created_at, 'dd.MM.yy HH:mm')}
														</td>
													</tr>
													{group.products.map((item, index) => (
														<tr key={index}>
															<td className="w-50" style={{ 'verticalAlign': 'top', 'whiteSpace': 'break-spaces' }}>
																{index + 1}. {item.name}
															</td>
															<td className="text-left text-nowrap" style={{ 'verticalAlign': 'top' }}>
																{formatMoney(item.quantity, 2)} X {formatMoney(item.sale_price)}
															</td>
															<td className="text-end text-nowrap text-danger" style={{ 'verticalAlign': 'top' }}>
																-{formatMoney(item.total_amount)}
															</td>
														</tr>
													))}
												</Fragment>
											))}
										</tbody>
									</table>
								</div>
							}

							<div className="cheque-block-payments cheque-border-bottom mb-2">
								<div className="d-flex justify-content-between">
									<p className="fw-600">{t('amount')}</p>
									<p>{formatMoney(selectedItem.total_amount)}</p>
								</div>
								{Number(selectedItem.service_percentage) > 0 &&
									<div className="d-flex justify-content-between">
										<p className="fw-600">{t('service')} {selectedItem.service_percentage ? selectedItem.service_percentage : 0}%</p>
										<p className="text-success">{formatMoney(selectedItem.service_amount)}</p>
									</div>
								}
								{!!selectedItem.delivery_price &&
									<div className="d-flex justify-content-between">
										<p className="fw-600">{t('delivery')} [km] {selectedItem.delivery_distance}</p>
										<p className="text-success">{formatMoney(selectedItem.delivery_price)}</p>
									</div>
								}
								{Number(selectedItem.discount) > 0 &&
									<div className="d-flex justify-content-between">
										<p className="fw-600">{t('discount')}</p>
										<p className="text-danger">{formatMoney(selectedItem.discount)}</p>
									</div>
								}
								{Number(selectedItem.surcharge) > 0 &&
									<div className="d-flex justify-content-between">
										<p className="fw-600">{t('surcharge')}</p>
										<p className="text-danger">{formatMoney(selectedItem.surcharge)}</p>
									</div>
								}
								<div className="d-flex justify-content-between">
									<p className="fw-600 fz-30">{t('to_pay')}</p>
									<p className="fw-600 fz-30">{formatMoney(selectedItem.total_amount_service)}</p>
								</div>

								{/* Возврат оформляется отдельным документом и сумму чека не переписывает —
									поэтому показываем его строкой под итогом вместе с суммой после возврата */}
								{Number(selectedItem.returned_amount) > 0 &&
									<>
										<div className="d-flex justify-content-between">
											<p className="fw-600">{t('returned')}</p>
											<p className="fw-600 text-danger">-{formatMoney(selectedItem.returned_amount)}</p>
										</div>
										<div className="d-flex justify-content-between">
											<p className="fw-600">{t('total_after_return')}</p>
											<p className="fw-600">{formatMoney(selectedItem.total_amount_after_return)}</p>
										</div>
									</>
								}

								{Number(selectedItem.paid) > 0 &&
									<div className="d-flex justify-content-between">
										<p className="fw-600">{t('paid')}</p>
										<p className="fw-600">{formatMoney(selectedItem.paid)}</p>
									</div>
								}

								{selectedItem?.payments?.map((item, index) => (
									<div className="d-flex justify-content-between" key={index}>
										{Number(item.amount) !== 0 ?
											<>
												<p>{item.payment_type_name}</p>
												<p>{formatMoney(item.amount)}</p>
											</>
											:
											<>
												<p>{item.payment_purpose_name}</p>
												<p>{formatMoney(item.amount)}</p>
											</>
										}
									</div>
								))}
								{Number(selectedItem.entity_debtor_amount) > 0 &&
									<div className="d-flex justify-content-between">
										<p>{t('debtor')}</p>
										<p>{selectedItem.entity_name}</p>
									</div>
								}
								{selectedItem.entity_debtor_amount > 0 &&
									<div className="d-flex justify-content-between">
										<p>{t('amount_of_debt')}</p>
										<p>{formatMoney(selectedItem.entity_debtor_amount)}</p>
									</div>
								}

								{Number(selectedItem.loyalty) > 0 &&
									<div className="d-flex justify-content-between">
										<p>{t('bonuses_earned')}</p>
										<p className="text-success">{formatMoney(selectedItem.loyalty)}</p>
									</div>
								}

								{(selectedItem.client_id && selectedItem.client_name) &&
									<div className="d-flex justify-content-between">
										<p>{t('client')}</p>
										<p>{selectedItem.client_name}</p>
									</div>
								}

								{(selectedItem.client_id && selectedItem.client_phone) &&
									<div className="d-flex justify-content-between">
										<p>{t('phone')}</p>
										<p>{selectedItem.client_phone}</p>
									</div>
								}
								{(selectedItem.client_id && selectedItem.client_phone2) &&
									<div className="d-flex justify-content-between">
										<p>{t('phone')}2</p>
										<p>{selectedItem.client_phone2}</p>
									</div>
								}
								{selectedItem.note &&
									<div className="d-flex justify-content-between">
										<p>{t('note')}</p>
										<p className="text-end">{selectedItem.note}</p>
									</div>
								}
								{selectedItem.apartment &&
									<div className="d-flex justify-content-between">
										<p>{t('apartment')}</p>
										<p className="text-end">{selectedItem.apartment}</p>
									</div>
								}
								{selectedItem.floor &&
									<div className="d-flex justify-content-between">
										<p>{t('floor')}</p>
										<p className="text-end">{selectedItem.floor}</p>
									</div>
								}
								{selectedItem.entrance &&
									<div className="d-flex justify-content-between">
										<p>{t('entrance')}</p>
										<p className="text-end">{selectedItem.entrance}</p>
									</div>
								}
								{selectedItem.intercom &&
									<div className="d-flex justify-content-between">
										<p>{t('intercom')}</p>
										<p className="text-end">{selectedItem.intercom}</p>
									</div>
								}
								{selectedItem.navigate &&
									<div className="d-flex justify-content-between">
										<p>{t('navigate')}</p>
										{selectedItem.navigate &&
											<p className="text-end">{selectedItem.navigate}</p>
										}
									</div>
								}
								{(selectedItem.address_id && selectedItem.client_address) &&
									<div className="d-flex justify-content-between">
										<p>{t('address')}</p>
										{selectedItem.client_address &&
											<p className="text-end">{selectedItem.client_address}</p>
										}
									</div>
								}

								{!!selectedItem.external_id &&
									<>
										<div className="d-flex justify-content-between">
											<p>SERVICE</p>
											<p>{selectedItem.integration_name}</p>
										</div>
										<div className="d-flex justify-content-between">
											<p>SERVICE ID</p>
											<p>{selectedItem.external_id}</p>
										</div>
										{/* Кто вёз заказ агрегатора — от этого зависит ставка его комиссии */}
										{selectedItem.external_delivery_mode &&
											<div className="d-flex justify-content-between">
												<p>{t('delivery_type')}</p>
												<p>{t('delivery_mode_' + selectedItem.external_delivery_mode)}</p>
											</div>
										}
									</>
								}

								{/* FISCAL INFO */}
								{selectedItem?.fiscal_terminal_id &&
									<div className="d-flex justify-content-between fz14">
										<p>{t('terminal')}</p>
										<p>{selectedItem?.fiscal_terminal_id}</p>
									</div>
								}
								{selectedItem?.fiscal_sign &&
									<div className="d-flex justify-content-between fz14">
										<p>{t('fiscal_sign')}</p>
										<p>{selectedItem?.fiscal_sign}</p>
									</div>
								}
								{selectedItem?.fiscal_qr_code &&
									<div className="d-flex justify-content-center">
										<QRCode value={selectedItem?.fiscal_qr_code} size={140} />
									</div>
								}
								{/* FISCAL INFO */}
								<div style={{ 'lineHeight': '14px' }}>
									{selectedItem.deletion_reason_name &&
										<div className="fz14">
											<b className="me-2">{t('deletion_reason')}:</b>
											<span>{selectedItem.deletion_reason_name}</span>
										</div>
									}
									{selectedItem.deletion_reason_comment &&
										<div className="fz14">
											<b className="me-2">{t('comment')}:</b>
											<span>{selectedItem.deletion_reason_comment}</span>
										</div>
									}
								</div>

								{!!selectedItem.cheque_number &&
									<div className="d-flex flex-column">
										<b className="text-center fz-24">{t('order_number')}</b>
										<b className="text-center fz-24">{selectedItem?.cheque_number}</b>
									</div>
								}
							</div>

							{/* Статусы чека (готовка, упаковка, в пути) и статусы заявки внешней
								службы — разные процессы, поэтому идут отдельными блоками */}
							{selectedItem?.statuses?.length > 0 &&
								<div className="fw-600 text-muted mb-1">{t('order_status')}</div>
							}
							{selectedItem?.statuses?.map((item, index) => (
								<div key={index}>
									<div className="d-flex justify-content-between">
										<b>{item.name}:</b>
										<div className="d-flex gap-3">
											<div>{formatDate(item.created_at, 'dd.MM.yyyy HH:mm:ss')}</div>
											<b style={{ 'width': '40px' }}>{item.duration}</b>
										</div>
									</div>
								</div>
							))}

							{!!selectedItem.external_courier_id && selectedItem?.courier_statuses?.length > 0 &&
								<>
									<hr className="my-2" />
									<div className="fw-600 text-muted mb-1">
										{t('courier_status')}: {selectedItem.external_courier_name ?? t('external_courier')}
									</div>
									{selectedItem.courier_statuses.map((item, index) => (
										<div key={index}>
											<div className="d-flex justify-content-between">
												<b>{t(externalCourierHistoryKey(item.status))}:</b>
												<div className="d-flex gap-3">
													<div>{formatDate(item.created_at, 'dd.MM.yyyy HH:mm:ss')}</div>
													<b style={{ 'width': '40px' }}>{courierStatusDuration(selectedItem.courier_statuses, index)}</b>
												</div>
											</div>
										</div>
									))}
								</>
							}

							<div className="pb-2"></div>

							<table className="table table-bordered">
								<thead>
									<tr>
										<th className="text-center" style={{ 'width': '33px' }}>#</th>
										<th style={{ 'width': '100px' }}><b>{t('date')}</b></th>
										<th style={{ 'width': '100px' }}><b>{t('user')}</b></th>
										<th><b>{t('action')}</b></th>
									</tr>
								</thead>
								<tbody>
									{selectedItem?.logs?.map((item, index) => (
										<tr key={index}>
											<td className="text-center">{index + 1}</td>
											<td>{formatDate(item.created_at, 'dd.MM HH:mm')}</td>
											<td className="td-ellipsis">
												<div title={item.created_by}>{item.created_by}</div>
												{/* Запись пришла не с кассы от сотрудника — показываем откуда */}
												{item.source_type &&
													<div className="fst-italic" title={logSourceLabel(item)}>{logSourceLabel(item)}</div>
												}
												{item?.params?.device_name &&
													<div className="text-muted fz12" title={item.params.device_name}>{item.params.device_name}</div>
												}
											</td>
											<td>
												<div dangerouslySetInnerHTML={{ __html: generateLogMessage(item.params) }} />
											</td>
										</tr>
									))}
								</tbody>
							</table>

							<div className="pb-5"></div>
						</div>

						<div className="px-2"
							style={{
								'position': 'absolute',
								'top': '15px'
							}}>
							<div className="d-flex gap-2">
								<div className="table-action-button table-action-primary-button"
									onClick={handlePrint}>
									<i className="uil uil-print"></i>
								</div>
							</div>
						</div>
					</div>

					<div style={{ display: "none" }}>
						<div id="cheque" ref={printRef} className="color-dark-theme">
							<div className="d-flex justify-content-center mt-3 mb-1">
								<div className="d-flex flex-column">
									<h5 className="text-center fw-700 mb-1">{t('duplicate')}</h5>
									<h5 className="text-center fw-700 mb-1">{selectedItem.pos_name}</h5>
									<h6 className="text-center fw-600 mb-1">{t('phone')}: {selectedItem.pos_phone}</h6>
									<h6 className="text-center fw-500 mb-1">{t('address')}: {selectedItem.pos_address}</h6>
								</div>
							</div>

							<div className="cheque-block-1 cheque-border-bottom mb-2">
								<div className="d-flex justify-content-between px-2">
									<p className="fw-600">{t('cashier')}</p>
									<p>{selectedItem.cashier_first_name}</p>
								</div>
								<div className="d-flex justify-content-between px-2">
									<p className="fw-600">{t('waiter')}</p>
									<p>{selectedItem.waiter_first_name}</p>
								</div>
								<div className="d-flex justify-content-between px-2">
									<p className="fw-600">{t('table')}</p>
									<p>{selectedItem.table_name}</p>
								</div>
								<div className="d-flex justify-content-between px-2">
									<p className="fw-600">{t('cheque')} № </p>
									<p>{selectedItem.cheque_number}</p>
								</div>
								<div className="d-flex justify-content-between px-2">
									<p className="fw-600">{t('guests')}</p>
									<p>{selectedItem.people_number}</p>
								</div>
								{selectedItem.tin &&
									<div className="d-flex justify-content-between px-2">
										<p className="fw-600">{t('tin')}</p>
										<p>{selectedItem.tin}</p>
									</div>
								}
								<div className="d-flex justify-content-between px-2">
									<p className="fw-600">{t('order_type')} </p>
									{selectedItem.order_type_name}
								</div>
								<div className="d-flex justify-content-between px-2">
									<p className="fw-600">{t('delivery_type')} </p>
									{selectedItem.order_type_name}
								</div>
								<div className="d-flex justify-content-between px-2">
									<p className="fw-600">{t('open_date')}</p>
									<p>{formatDate(selectedItem.cheque_opened_at, 'dd.MM.yy HH:mm')}</p>
								</div>
								<div className="d-flex justify-content-between px-2">
									<p className="fw-600">{t('close_date')}</p>
									<p>{formatDate(selectedItem.cheque_closed_at, 'dd.MM.yy HH:mm')}</p>
								</div>
							</div>

							<div className="cheque-block-2 cheque-border-bottom px-2">
								<table className="custom-cheque-table w-100 fz-14">
									<thead>
										<tr>
											<th className="w-50">№ {t('product')}</th>
											<th>{t('quantity')}</th>
											<th className="text-end">{t('price')}</th>
										</tr>
									</thead>
									<tbody>
										{selectedItem?.products?.length > 0 &&
											<>
												{composeConstructorLines(selectedItem?.products)?.map((item, index) => (
													<tr className={`${item.status === -1 ? 'del' : ''}`} key={index}>
														{/* column 1 */}
														<td style={{ 'whiteSpace': 'break-spaces' }}>
															<div>{index + 1} {item.name}{priceKindLabel(item) ? ` (${priceKindLabel(item)})` : ''}</div>
															{!!compositionText(item, composeLabels) && <div className="ms-2 fz12">{compositionText(item, composeLabels)}</div>}
														</td>
														{/* column 1 */}

														{/* column 2 */}
														<td className="text-left" style={{ 'verticalAlign': 'top' }}>
															<span className="text-nowrap" >{formatMoney(item.quantity)} X {formatMoney(item.sale_price)}</span>
														</td>
														{/* column 2 */}

														{/* column 3 */}
														<td className="text-end text-nowrap">
															{formatMoney(item.total_price)}
														</td>
														{/* column 3 */}
													</tr>
												))}
											</>
										}
									</tbody>
								</table>
							</div>

							{selectedItem?.returns_products?.length > 0 &&
								<div className="cheque-block-2 cheque-border-bottom mb-2 px-2">
									<p className="fw-600 mb-1">{t('returned_products')}</p>
									{/* Та же таблица, что и у состава чека — иначе колонки разъезжаются */}
									<table className="custom-cheque-table w-100 fz-14">
										<tbody>
											{groupReturns(selectedItem.returns_products).map((group) => (
												<Fragment key={group.id}>
													<tr>
														<td colSpan={3} className="fz12 text-secondary">
															{formatDate(group.created_at, 'dd.MM.yy HH:mm')}
														</td>
													</tr>
													{group.products.map((item, index) => (
														<tr key={index}>
															<td className="w-50" style={{ 'verticalAlign': 'top', 'whiteSpace': 'break-spaces' }}>
																{index + 1}. {item.name}
															</td>
															<td className="text-left text-nowrap" style={{ 'verticalAlign': 'top' }}>
																{formatMoney(item.quantity, 2)} X {formatMoney(item.sale_price)}
															</td>
															<td className="text-end text-nowrap text-danger" style={{ 'verticalAlign': 'top' }}>
																-{formatMoney(item.total_amount)}
															</td>
														</tr>
													))}
												</Fragment>
											))}
										</tbody>
									</table>
								</div>
							}

							<div className="cheque-block-3 cheque-border-bottom mb-2">
								<div className="d-flex justify-content-between px-2">
									<p className="fw-600">{t('sales_amount')}</p>
									<p>{formatMoney(selectedItem.total_amount)}</p>
								</div>
								{selectedItem.order_type === 1 &&
									<div className="d-flex justify-content-between px-2">
										<p className="fw-600">{t('service')} {selectedItem.service_percentage ? selectedItem.service_percentage : 0}%</p>
										<p>{formatMoney(selectedItem.total_amount * (selectedItem.service_percentage / 100))}</p>
									</div>
								}
								{!!selectedItem.delivery_price &&
									<div className="d-flex justify-content-between px-2">
										<p className="fw-600">{t('delivery')} [km] {selectedItem.delivery_distance}</p>
										<p>{formatMoney(selectedItem.delivery_price)}</p>
									</div>
								}
								{Number(selectedItem.discount) > 0 &&
									<div className="d-flex justify-content-between px-2">
										<p className="fw-600">{t('discount')}</p>
										<p>{formatMoney(selectedItem.discount)}</p>
									</div>
								}
								{Number(selectedItem.agent_amount) > 0 &&
									<div className="d-flex justify-content-between px-2">
										<p className="fw-600">{t('agents_bonus')}</p>
										<p className="fw-600">{formatMoney(selectedItem.agent_amount)}</p>
									</div>
								}
								<div className="d-flex justify-content-between px-2">
									<p className="fw-600 fz-30">{t('to_pay')}</p>
									<p className="fw-600 fz-30">{formatMoney(selectedItem.total_amount_service)}</p>
								</div>

								{Number(selectedItem.returned_amount) > 0 &&
									<>
										<div className="d-flex justify-content-between px-2">
											<p className="fw-600">{t('returned')}</p>
											<p className="fw-600 text-danger">-{formatMoney(selectedItem.returned_amount)}</p>
										</div>
										<div className="d-flex justify-content-between px-2">
											<p className="fw-600">{t('total_after_return')}</p>
											<p className="fw-600">{formatMoney(selectedItem.total_amount_after_return)}</p>
										</div>
									</>
								}

								{Number(selectedItem.paid) > 0 &&
									<div className="d-flex justify-content-between px-2">
										<p className="fw-600">{t('paid')}</p>
										<p className="fw-600">{formatMoney(selectedItem.paid)}</p>
									</div>
								}

								{selectedItem?.payments?.map((item, index) => (
									<div className="d-flex justify-content-between px-2" key={index}>
										{Number(item.amount) !== 0 ?
											<>
												<p>{item.payment_type_name}</p>
												<p>{formatMoney(item.amount)}</p>
											</>
											:
											<>
												<p>{item.payment_purpose_name}</p>
												<p>{formatMoney(item.amount)}</p>
											</>
										}
									</div>
								))}
								{Number(selectedItem.entity_debtor_amount) > 0 &&
									<div className="d-flex justify-content-between">
										<p>{t('debtor')}</p>
										<p>{selectedItem.entity_name}</p>
									</div>
								}
								{selectedItem.entity_debtor_amount > 0 &&
									<div className="d-flex justify-content-between">
										<p>{t('debt')}</p>
										<p>{formatMoney(selectedItem.entity_debtor_amount)}</p>
									</div>
								}

								{Number(selectedItem.loyalty) > 0 &&
									<div className="d-flex justify-content-between px-2">
										<p>{t('loyalty')}</p>
										<p>{formatMoney(selectedItem.loyalty)}</p>
									</div>
								}

								{(selectedItem.client_id && selectedItem.client_name) &&
									<div className="d-flex justify-content-between px-2">
										<p>{t('client')}</p>
										{selectedItem.client_name &&
											<p>{selectedItem.client_name}</p>
										}
									</div>
								}

								{(selectedItem.client_id && selectedItem.client_phone) &&
									<div className="d-flex justify-content-between px-2">
										<p>{t('phone')}</p>
										{selectedItem.client_phone &&
											<p>{selectedItem.client_phone}</p>
										}
									</div>
								}
								{(selectedItem.client_id && selectedItem.client_phone2) &&
									<div className="d-flex justify-content-between px-2">
										<p>{t('phone')}2</p>
										{selectedItem.client_phone2 &&
											<p>{selectedItem.client_phone2}</p>
										}
									</div>
								}
								{selectedItem.note &&
									<div className="d-flex justify-content-between">
										<p>{t('note')}</p>
										{selectedItem.note &&
											<p className="text-end">{selectedItem.note}</p>
										}
									</div>
								}
								{(selectedItem.apartment || selectedItem.floor) &&
									<div className="d-flex gap-5 fz16">
										{selectedItem.apartment &&
											<div className="d-flex justify-content-between" style={{ 'width': '110px' }}>
												<b>{t('apartment')}:</b>
												<span>{selectedItem.apartment}</span>
											</div>
										}
										{selectedItem.floor &&
											<div className="d-flex justify-content-between" style={{ 'width': '110px' }}>
												<b>{t('floor')}:</b>
												<span>{selectedItem.floor}</span>
											</div>
										}
									</div>
								}
								{(selectedItem.entrance || selectedItem.intercom) &&
									<div className="d-flex gap-5 fz16">
										{selectedItem.entrance &&
											<div className="d-flex justify-content-between" style={{ 'width': '110px' }}>
												<b>{t('entrance')}:</b>
												<span>{selectedItem.entrance}</span>
											</div>
										}
										{selectedItem.intercom &&
											<div className="d-flex justify-content-between" style={{ 'width': '110px' }}>
												<b>{t('intercom')}:</b>
												<span>{selectedItem.intercom}</span>
											</div>
										}
									</div>
								}
								{selectedItem.navigate &&
									<div className="d-flex justify-content-between">
										<p>{t('navigate')}</p>
										{selectedItem.navigate &&
											<p className="text-end">{selectedItem.navigate}</p>
										}
									</div>
								}
								{(selectedItem.address_id && selectedItem.client_address) &&
									<div className="d-flex justify-content-between px-2">
										<p>{t('address')}</p>
										{selectedItem.client_address &&
											<p className="text-end">{selectedItem.client_address}</p>
										}
									</div>
								}
							</div>

							{/* FISCAL INFO */}
							{selectedItem?.fiscal_terminal_id &&
								<div className="d-flex justify-content-between fz14">
									<p>{t('terminal')}</p>
									<p>{selectedItem?.fiscal_terminal_id}</p>
								</div>
							}
							{selectedItem?.fiscal_sign &&
								<div className="d-flex justify-content-between fz14">
									<p>{t('fiscal_sign')}</p>
									<p>{selectedItem?.fiscal_sign}</p>
								</div>
							}
							{selectedItem?.fiscal_qr_code &&
								<div className="d-flex justify-content-center">
									<QRCode value={selectedItem?.fiscal_qr_code} size={140} />
								</div>
							}
							{/* FISCAL INFO */}
						</div>
					</div>
				</>
			}
		</>
	)
}

export default Rightbar
